import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "results", "hiddenField", "selectedInfo", "clearButton"]
  static values = { 
    url: String,
    minChars: { type: Number, default: 2 },
    delay: { type: Number, default: 300 }
  }

  connect() {
    this.searchTimeout = null
    this.abortController = null
    this.setupEventListeners()

    // Show selected info if there's already a value
    if (this.hasHiddenFieldTarget && this.hiddenFieldTarget.value) {
      this.showClearButton()
    }
  }

  disconnect() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    if (this.abortController) {
      this.abortController.abort()
    }
  }

  cancelPendingRequests() {
    if (this.abortController) {
      this.abortController.abort()
    }
    this.abortController = new AbortController()
    return this.abortController.signal
  }

  setupEventListeners() {
    // Close results when clicking outside
    document.addEventListener('click', this.handleOutsideClick.bind(this))
    
    // Close results when pressing Escape
    this.inputTarget.addEventListener('keydown', this.handleKeydown.bind(this))
  }

  handleOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.hideResults()
    }
  }
  
  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.hideResults()
    }
  }

  search() {
    const query = this.inputTarget.value.trim()

    // Clear any existing timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    // If input is empty, show initial clients
    if (query.length === 0) {
      this.loadInitialClients()
      return
    }

    // Hide results if query is too short for search
    if (query.length < this.minCharsValue) {
      this.cancelPendingRequests()
      this.hideResults()
      return
    }

    // Debounce the search
    this.searchTimeout = setTimeout(() => {
      this.performSearch(query)
    }, this.delayValue)
  }

  focus() {
    // Skip if a client is already selected
    if (this.hasHiddenFieldTarget && this.hiddenFieldTarget.value) {
      return
    }

    // Skip if input has text (user is searching)
    if (this.inputTarget.value.trim().length > 0) {
      return
    }

    // Load initial clients on first focus
    this.loadInitialClients()
  }

  async loadInitialClients() {
    this.showLoading()

    const searchUrl = this.urlValue || '/oms/clients/search'
    const signal = this.cancelPendingRequests()

    try {
      const csrfToken = document.querySelector('[name="csrf-token"]')?.content
      const response = await fetch(searchUrl, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': csrfToken
        },
        signal
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      this.showResults(data.results || data)
    } catch (error) {
      if (error.name === 'AbortError') {
        return // Request was cancelled, do nothing
      }
      console.error('Failed to load initial clients:', error)
      this.showError()
    }
  }

  async performSearch(query) {
    // Show loading state
    this.showLoading()

    const searchUrl = this.urlValue || '/oms/clients/search'
    const signal = this.cancelPendingRequests()

    try {
      const csrfToken = document.querySelector('[name="csrf-token"]')?.content
      const response = await fetch(`${searchUrl}?q=${encodeURIComponent(query)}`, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': csrfToken
        },
        signal
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      this.showResults(data.results || data)
    } catch (error) {
      if (error.name === 'AbortError') {
        return // Request was cancelled, do nothing
      }
      console.error('Client search failed:', error)
      this.showError()
    }
  }

  showLoading() {
    this.resultsTarget.innerHTML = `
      <div class="px-4 py-3 text-sm text-neutral-500 flex items-center">
        <svg class="animate-spin h-4 w-4 mr-2 text-neutral-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Searching...
      </div>
    `
    this.resultsTarget.classList.remove('hidden')
  }

  showResults(clients) {
    if (clients.length === 0) {
      this.resultsTarget.innerHTML = `
        <div class="px-4 py-3 text-sm text-neutral-500">
          No clients found
        </div>
      `
    } else {
      this.resultsTarget.innerHTML = clients.map(client => this.clientResultHtml(client)).join('')
    }
    
    this.resultsTarget.classList.remove('hidden')
  }

  showError() {
    this.resultsTarget.innerHTML = `
      <div class="px-4 py-3 text-sm text-error-600">
        An error occurred while searching. Please try again.
      </div>
    `
    this.resultsTarget.classList.remove('hidden')
  }

  hideResults() {
    // Cancel any pending debounced search and in-flight requests
    // to prevent them from re-opening the dropdown after it's hidden
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    if (this.abortController) {
      this.abortController.abort()
    }
    this.resultsTarget.classList.add('hidden')
  }

  selectClient(event) {
    event.preventDefault()
    const button = event.currentTarget
    const clientData = JSON.parse(button.dataset.client)
    
    // Set the hidden field value
    if (this.hasHiddenFieldTarget) {
      this.hiddenFieldTarget.value = clientData.id
    }
    
    // Update the input display
    this.inputTarget.value = clientData.name
    
    // Show selected client info
    this.showSelectedInfo(clientData)
    
    // Hide results
    this.hideResults()
    
    // Show clear button
    this.showClearButton()
    
    // Trigger filter change event for auto-submit
    this.hiddenFieldTarget.dispatchEvent(new CustomEvent('clientSelected', { detail: clientData }))
    
    // Return focus to input
    this.inputTarget.focus()
  }

  showSelectedInfo(client) {
    if (this.hasSelectedInfoTarget) {
      const badgeInfo = this.getBadgeInfo(client)
      this.selectedInfoTarget.innerHTML = `
        <div class="mt-2 p-3 bg-neutral-50 rounded-md border border-neutral-200">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="font-medium text-neutral-900">${client.name}</p>
              ${client.email ? `<p class="text-sm text-neutral-600">${client.email}</p>` : ''}
              ${client.provider ? `<p class="text-sm text-neutral-600">${this.formatProvider(client.provider)} Account</p>` : ''}
              ${client.phone ? `<p class="text-sm text-neutral-600">${client.phone}</p>` : ''}
            </div>
            <div class="flex items-center space-x-2">
              <span class="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${badgeInfo.classes}">
                ${badgeInfo.text}
              </span>
              <button type="button"
                      class="text-neutral-400 hover:text-neutral-600"
                      data-action="click->oms--oms--client-search#clearSelection">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      `
      this.selectedInfoTarget.classList.remove('hidden')
    }
  }

  showClearButton() {
    if (this.hasClearButtonTarget) {
      this.clearButtonTarget.classList.remove('hidden')
    }
  }

  hideClearButton() {
    if (this.hasClearButtonTarget) {
      this.clearButtonTarget.classList.add('hidden')
    }
  }

  clearSelection() {
    // Clear all fields
    if (this.hasHiddenFieldTarget) {
      this.hiddenFieldTarget.value = ''
    }
    this.inputTarget.value = ''
    
    // Hide selected info
    if (this.hasSelectedInfoTarget) {
      this.selectedInfoTarget.innerHTML = ''
      this.selectedInfoTarget.classList.add('hidden')
    }
    
    // Hide clear button
    this.hideClearButton()
    
    // Trigger filter change event for auto-submit
    if (this.hasHiddenFieldTarget) {
      this.hiddenFieldTarget.dispatchEvent(new CustomEvent('clientCleared'))
    }
    
    // Focus input
    this.inputTarget.focus()
  }

  clientResultHtml(client) {
    const badgeInfo = this.getBadgeInfo(client)
    return `
      <button type="button"
              class="w-full text-left px-4 py-3 hover:bg-neutral-50 flex items-center justify-between transition-colors"
              data-action="click->oms--oms--client-search#selectClient"
              data-client='${JSON.stringify(client).replace(/'/g, '&apos;')}'>
        <div>
          <p class="font-medium text-neutral-900">${this.escapeHtml(client.name)}</p>
          ${client.email ? `<p class="text-sm text-neutral-600">${this.escapeHtml(client.email)}</p>` : ''}
        </div>
        <div class="flex items-center space-x-2">
          ${client.phone ? `<span class="text-sm text-neutral-500">${this.escapeHtml(client.phone)}</span>` : ''}
          <span class="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${badgeInfo.classes}">
            ${badgeInfo.text}
          </span>
        </div>
      </button>
    `
  }

  getBadgeInfo(client) {
    // If client cannot settle via TD, show "read only" warning badge
    if (client.can_settle_via_td === false) {
      return {
        text: 'read only',
        classes: 'bg-amber-100 text-amber-700'
      }
    }

    // Otherwise show status-based badge
    if (client.status === 'active') {
      return {
        text: 'active',
        classes: 'bg-success-100 text-success-700'
      }
    }

    return {
      text: client.status,
      classes: 'bg-neutral-100 text-neutral-700'
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  formatProvider(provider) {
    // Convert provider name to title case
    // e.g., 'austraclear' -> 'Austraclear', 'treasury_direct' -> 'Treasury Direct'
    return provider
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }
}
